import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { AspectRatio } from "@/components/ui/aspect-ratio";
import DynamicTitle from '@/components/DynamicTitle';
import SEOStructuredData from '@/components/SEOStructuredData';

export default function PromptManager() {
  return (
    <div className="min-h-screen bg-black">
      <DynamicTitle 
        title="Prompt Manager - How I Built a SaaS Without Coding"
        description="The story of how Aram AI built Prompt Manager, a SaaS platform for organizing AI prompts, using vibe coding and no traditional programming skills."
        keywords="Prompt Manager, SaaS development, vibe coding, AI prompts, no-code SaaS, entrepreneurship, startup story"
        canonicalUrl="https://aramb.am/prompt-manager"
      />
      <SEOStructuredData 
        type="article"
        title="How I Built a SaaS Without Coding: The Prompt Manager Story"
        description="The complete story of building Prompt Manager SaaS using vibe coding methodology and AI tools."
        url="https://aramb.am/prompt-manager"
        image="https://aramb.am/lovable-uploads/89aabc24-d8c9-4bec-a9a2-a59436093533.png"
        author="Aram AI"
        datePublished="2024-12-17"
      />
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-heading glow-text">
            How I Built a SaaS Without Coding: The Prompt Manager Story
          </h1>
        </div>

        {/* Hero Image */}
        <div className="mb-12">
          <AspectRatio ratio={16/9} className="overflow-hidden rounded-lg">
            <img 
              src="/lovable-uploads/89aabc24-d8c9-4bec-a9a2-a59436093533.png" 
              alt="Prompt Manager Interface" 
              className="w-full h-full object-cover"
            />
          </AspectRatio>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto mb-24">
          <article className="glass-panel rounded-2xl overflow-hidden">
            <div className="p-12 space-y-8">
              <div className="prose prose-invert prose-xl max-w-none leading-relaxed">
                <p className="text-xl leading-[1.8] text-gray-200 font-light mb-8 first-letter:text-6xl first-letter:font-bold first-letter:text-purple-300 first-letter:float-left first-letter:mr-3 first-letter:mt-1">
                  It happened all the time. I'd work to get that perfect AI prompt – the one that gave me the exact great answer I wanted. And then, it would just disappear online, lost in long chat histories or in different notes. When I needed that specific prompt again? Gone. I was very frustrated, and I wasted even more time. I knew there had to be a better way.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  Finding that "better way" wasn't some big, carefully planned business idea. It was more like an accident, an idea that started because I was curious. One day, looking through AI communities online, I found a new tool called <span className="font-bold text-white">Lovable.dev</span>. It was pretty new and said it could help you build real products using AI. The idea that you could make software without being a coder got me interested. I thought, "Okay… what could I build with this?"
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  I started playing with it, just trying things out. The first idea wasn't huge; it was just to fix my own annoying problem: I needed one place to save my best AI prompts so I could find and use them easily.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  What happened next still surprises me. For a couple of weeks, I got really into what we later called <span className="font-bold text-white italic">"vibe coding."</span> Instead of struggling with difficult code, I was just telling the AI what to do – to design how it works, create the parts, and put together a real app. It wasn't about fixing code errors; it was about explaining my idea clearly. And I really enjoyed doing it. It felt creative and like I could really build something.
                </p>

                <div className="bg-purple-900/20 border-l-4 border-purple-400 pl-6 py-4 my-8 rounded-r-lg">
                  <p className="text-lg leading-[1.8] text-gray-200 italic">
                    After about two weeks, working a few hours each day, it was done! I had a real SaaS product. It wasn't just a test version; it had Google sign-in, Stripe for payments (if it ever got there), and a Supabase database. I made a logo, bought a domain, and just like that, the thing I started calling Prompt Manager was live.
                  </p>
                </div>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  The main idea of Prompt Manager was simple: to be an easy way to save, organize, and share AI prompts, so those good ideas were never lost again. It was the solution I really needed for myself.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  I shared it online, not expecting much. I just hoped it might help other people with the same problem. But more people used it than I thought. Soon, <span className="font-bold text-white">over 500 people had signed up</span>. Then, something I didn't expect at all: Prompt Manager got noticed in a competition by Lovable! It was a nice surprise and showed that people liked it.
                </p>

                <div className="text-center my-10">
                  <div className="inline-block bg-gradient-to-r from-purple-600/20 to-blue-600/20 p-8 rounded-xl border border-purple-500/30">
                    <p className="text-2xl font-semibold text-purple-200 mb-4">The Magic Moment</p>
                    <div className="mb-4">
                      <img 
                        src="/lovable-uploads/690780bc-0a9a-4398-97b0-afd4a6b37ec6.png" 
                        alt="First paying customer payment confirmation" 
                        className="rounded-lg max-w-full h-auto mx-auto"
                      />
                    </div>
                    <p className="text-lg text-gray-300">My first paying customer: $40 for a whole year</p>
                  </div>
                </div>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  But the moment that really meant a lot to me came later: I got my first paying customer. It wasn't a lot of money – just $40 for a whole year. But I was genuinely excited. That small amount felt huge. It was the first real proof from someone else that Prompt Manager solved a real problem they were willing to pay to fix. That one payment felt great because it showed this "vibe coded" project was actually useful to others.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  This whole experience with Prompt Manager taught me something big: you can build useful things, even a full SaaS product, starting from nothing, without being a coder, and without a big team or lots of money. The tools we have now are making it possible for more people to create things. If you have an idea and really want to fix a problem (even your own), you can make it happen.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-8">
                  That's the real story I wanted to share here. Prompt Manager shows this idea is true.
                </p>

                <div className="bg-black/40 border border-purple-500/30 rounded-xl p-8 my-10">
                  <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                    So, if you're someone who makes good AI prompts but then loses them, or if you're just curious about how a "vibe coded" tool for organizing prompts works, you can check out Prompt Manager. It's free to get started.
                  </p>
                  
                  <div className="text-center">
                    <a 
                      href="https://promptmanager.pro" 
                      target="_blank" 
                      rel="noreferrer" 
                      className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25"
                    >
                      Try the app
                    </a>
                  </div>
                </div>

                <p className="text-xl leading-[1.8] text-gray-200 font-medium">
                  More importantly, if you have an idea, something you want to build, I hope this story helps you try out the tools available and just begin. You might be surprised by what you can create.
                </p>
              </div>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
